import React, { useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { hasPerm } from '../../../utils/permissions';
import WorkTasksModule from './WorkTasksModule';
import TeacherStudents from './TeacherStudents';
import TeacherSchedule from './TeacherSchedule';
import MyLessonsTaught from './MyLessonsTaught';
import MyAvailability from './MyAvailability';
import WarmupsPage from '../../../pages/EmployeePortal/Warmups/WarmupsPage';
import TodayPage from '../../../pages/EmployeePortal/TodayPage';
import NotificationOptIn from '../NotificationOptIn';
import HelpPage from '../Guide/HelpPage';
import './TeacherDashboard.css';

// Phase E3b — the teacher dashboard. Renders inside the WorkShell teacher branch
// (PortalSpaceChrome single-space "Dashboard" framing). Deny-by-default: each
// section appears only if the teacher has been granted its permission (Phase E2
// toggles). A teacher with no grants lands on the welcome/empty state. This view
// deliberately renders NO leads / billing / admin / scheduling-rules / space
// switcher / other spaces — defense in depth alongside the E1 rules.
function TeacherDashboard() {
	const { currentUser, userProfile } = useAuth();
	const name =
		userProfile?.displayName ||
		currentUser?.email?.split('@')[0] ||
		'there';

	const tabs = useMemo(() => {
		const list = [];
		// TODAY IS FIRST BECAUSE IT IS THE ONLY TAB WITH A VERB IN IT. Until
		// this existed, a non-admin teacher had NO in-app path to a lesson at
		// all: /portal/work/lesson/:id is inside the employee|admin route tree
		// and works fine, but the click-through to it (Today → Start) lived
		// only in the admin PortalLayout sidebar, which the teacher branch of
		// WorkShell never renders. The route was reachable and unreachable at
		// the same time — by URL, not by hand.
		//
		// Gated on viewSchedule, which is the same permission the Firestore
		// rules require for every collection this page reads (lessons,
		// timeSlots, events, trialSlots) AND for the lessons CREATE that Start
		// performs when it mints a session record from a scheduled occurrence.
		// So the tab appearing and the page working are governed by one grant,
		// rather than the tab promising something the rules then deny.
		if (hasPerm(userProfile, 'viewSchedule'))
			list.push({ key: 'today', label: 'Today', icon: 'fa-calendar-day' });
		if (hasPerm(userProfile, 'manageTasks'))
			list.push({ key: 'tasks', label: 'Tasks', icon: 'fa-list-check' });
		if (hasPerm(userProfile, 'viewStudents'))
			list.push({
				key: 'students',
				label: 'Students',
				icon: 'fa-graduation-cap',
			});
		if (hasPerm(userProfile, 'viewSchedule'))
			list.push({
				key: 'schedule',
				label: 'Schedule',
				icon: 'fa-calendar-week',
			});
		// Same grant as Schedule: the tab reads teachers/teacherTimeOff (both
		// gated viewSchedule in rules) and writes only through the availability
		// callables, so one permission governs the tab appearing AND working.
		if (hasPerm(userProfile, 'viewSchedule'))
			list.push({
				key: 'availability',
				label: 'Availability',
				icon: 'fa-calendar-check',
			});
		// WARMUPS IS UNGATED, and it is the only tab here that is.
		//
		// The others gate on a capability because each reads student, schedule
		// or task data that firestore.rules also gates — the tab appearing and
		// the page working are governed by one grant. The warmup catalog is
		// studio TEACHING MATERIAL: `warmupExercises` is readable by any
		// employee with no capability key, so a permission check here would
		// hide a page the database would happily serve, which is the mirror of
		// the failure the other gates prevent.
		//
		// This tab is also the ONLY way Annelise reaches the surface. WorkShell
		// renders the admin sidebar for role 'admin' alone, so the portalNav
		// entry added alongside this reaches Grace and Anthony and nobody else;
		// without this tab the route would be reachable by URL and unreachable
		// by hand — exactly the gap the Today tab above was added to close.
		list.push({ key: 'warmups', label: 'Warmups', icon: 'fa-music' });
		// Help (the staff guide) is ungated for the same reason as Warmups: it
		// reads no data at all. Its guided tours point at the tabs above, so it
		// lives here, beside them, rather than behind a route Annelise has no
		// link to.
		list.push({ key: 'help', label: 'Help', icon: 'fa-circle-question' });
		return list;
	}, [userProfile]);

	// Whether ANY permission-gated section is on. Warmups is ungated, so
	// `tabs.length` stopped being the right question the moment it was added:
	// a brand-new teacher with no grants would have silently lost the "your
	// dashboard is being set up" notice and landed on a warmup catalog with no
	// explanation for why nothing else is there. She still gets Warmups — it is
	// hers to use — but she is still told the rest is coming.
	const hasGrantedSection = useMemo(
		() => tabs.some((t) => t.key !== 'warmups' && t.key !== 'help'),
		[tabs]
	);

	const [active, setActive] = useState(null);
	const activeKey = active && tabs.some((t) => t.key === active)
		? active
		: tabs[0]?.key;

	return (
		<div className='td-page'>
			<header className='td-head'>
				<div className='td-head__eyebrow'>Dashboard</div>
				<h1 className='td-head__title'>Welcome back, {name}</h1>
				{!hasGrantedSection && (
					<p className='td-head__sub'>
						Your teaching dashboard is being set up. Your students,
						schedule, and tasks will appear here once an admin turns
						them on.
					</p>
				)}
			</header>

			<NotificationOptIn />

			{tabs.length > 1 && (
				<div className='td-tabs' role='tablist'>
					{tabs.map((t) => (
						<button
							key={t.key}
							type='button'
							role='tab'
							data-guide={`dash-tab-${t.key}`}
							aria-selected={activeKey === t.key}
							className={`td-tab${
								activeKey === t.key ? ' is-active' : ''
							}`}
							onClick={() => setActive(t.key)}>
							<i className={`fa-sharp fa-solid ${t.icon}`} />
							<span>{t.label}</span>
						</button>
					))}
				</div>
			)}

			{tabs.length > 0 && (
				<div className='td-panel'>
					{/* The admin Today page, reused rather than reimplemented —
					    it already carries the Start button, the occurrence
					    expansion and the session-minting. Its PortalHeader
					    collapses to a slim title inside PortalSpaceChrome
					    (PortalShellContext), so there is no duplicated chrome. */}
					{activeKey === 'today' && <TodayPage />}
					{activeKey === 'tasks' && (
						<WorkTasksModule assigneeUid={currentUser?.uid} />
					)}
					{activeKey === 'students' && <TeacherStudents />}
					{/* Her own lessons-taught count sits with her schedule: a
					    count and the lessons behind it, never a cap or a rate. */}
					{activeKey === 'schedule' && (
						<>
							<MyLessonsTaught teacherId={userProfile?.teacherId} />
							<TeacherSchedule teacherId={userProfile?.teacherId} />
						</>
					)}
					{activeKey === 'availability' && (
						<MyAvailability teacherId={userProfile?.teacherId} />
					)}
					{activeKey === 'warmups' && <WarmupsPage />}
					{activeKey === 'help' && <HelpPage />}
				</div>
			)}
		</div>
	);
}

export default TeacherDashboard;
